import React, { useCallback, useEffect, useState } from "react";
import { AxiosError } from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "../utils/toast";
import AdminWorkspaceNav from "../components/admin/AdminWorkspaceNav";
import { useAuth } from "../contexts/AuthContext";
import {
  createAdminWalletCoupon,
  deleteAdminWalletCoupon,
  fetchAdminWalletCoupons,
  fetchAdminWalletCouponRedemptions,
  fetchAdminWalletSettings,
  grantAdminWalletCouponRedemptions,
  updateAdminWalletCoupon,
  updateAdminWalletSettings,
} from "../services/adminService";
import {
  WalletCoupon,
  WalletCouponPayload,
  WalletCouponRedemption,
  WalletSettings,
} from "../types/store";
import "../styles/pages/AdminDashboardPage.css";
import ActionSwitchPill from "../components/shared/ActionSwitchPill";

const emptyCoupon: WalletCouponPayload = {
  code: "",
  type: "WALLET_TOPUP",
  amount: 0,
  discountPercentage: 0,
  description: "",
  assignedCustomerEmails: "",
  active: true,
  rewardDelayMinutes: 60,
  redemptionFrequency: "ONCE",
};

const emptyWalletSettings: WalletSettings = {
  enableWalletUsage: true,
  enableWalletTopUp: true,
  allowWalletWithCoupons: true,
  minimumOrderAmountRequiredToUseWallet: 1000,
  walletUsageType: "PERCENTAGE",
  walletUsageValue: 10,
  maximumWalletAmountPerOrder: 500,
  minimumWalletBalanceRequired: null,
  enableWalletForCodOrders: true,
  enableWalletForOnlinePayments: true,
  enableWalletForPickUpOrders: true,
  enableWalletForHomeDeliveryOrders: true,
};

const extractErrorMessage = (error: unknown, fallback: string) => {
  if (error instanceof AxiosError) {
    return (error.response?.data as { message?: string } | undefined)?.message || fallback;
  }
  return fallback;
};

const walletSettingLabels: Record<
  "enableWalletUsage" | "enableWalletTopUp" | "allowWalletWithCoupons" | "enableWalletForCodOrders" | "enableWalletForPickUpOrders" | "enableWalletForHomeDeliveryOrders",
  string
> = {
  enableWalletUsage: "Wallet usage",
  enableWalletTopUp: "Wallet top-up",
  allowWalletWithCoupons: "Wallet with coupons",
  enableWalletForCodOrders: "Wallet for COD orders",
  enableWalletForPickUpOrders: "Wallet for pickup orders",
  enableWalletForHomeDeliveryOrders: "Wallet for home delivery orders",
};

const AdminWalletPage: React.FC = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [coupons, setCoupons] = useState<WalletCoupon[]>([]);
  const [selectedCouponId, setSelectedCouponId] = useState<number | null>(null);
  const [redemptions, setRedemptions] = useState<WalletCouponRedemption[]>([]);
  const [editingCouponId, setEditingCouponId] = useState<number | null>(null);
  const [couponForm, setCouponForm] = useState<WalletCouponPayload>(emptyCoupon);
  const [walletSettings, setWalletSettings] = useState<WalletSettings>(emptyWalletSettings);
  const [savingSettings, setSavingSettings] = useState(false);
  const [savingCoupon, setSavingCoupon] = useState(false);
  const [deletingCouponId, setDeletingCouponId] = useState<number | null>(null);
  const [grantingCouponId, setGrantingCouponId] = useState<number | null>(null);

  const handleAdminRequestError = useCallback((error: unknown, fallback: string) => {
    if (error instanceof AxiosError && error.response?.status === 401) {
      toast.error("Your admin session has expired. Please log in again.");
      logout();
      navigate("/admin/login", {
        replace: true,
        state: { from: location, adminOnly: true },
      });
      return;
    }

    if (error instanceof AxiosError && error.response?.status === 403) {
      toast.error(extractErrorMessage(error, "You do not have permission to manage wallet coupons."));
      return;
    }

    toast.error(extractErrorMessage(error, fallback));
  }, [location, logout, navigate]);

  const loadData = useCallback(async () => {
    try {
      const [walletCoupons, settings] = await Promise.all([
        fetchAdminWalletCoupons(),
        fetchAdminWalletSettings(),
      ]);
      setCoupons(walletCoupons);
      setSelectedCouponId((current) => current ?? walletCoupons[0]?.id ?? null);
      setWalletSettings({
        ...settings,
        enableWalletForOnlinePayments: false,
      });
    } catch (error) {
      handleAdminRequestError(error, "Unable to load wallet coupons.");
    }
  }, [handleAdminRequestError]);

  const saveWalletSettings = useCallback(
    async (nextSettings: WalletSettings, successMessage: string) => {
      setSavingSettings(true);
      try {
        await updateAdminWalletSettings({
          ...nextSettings,
          enableWalletForOnlinePayments: false,
        });
        setWalletSettings(nextSettings);
        toast.success(successMessage);
        await loadData();
      } catch (error) {
        handleAdminRequestError(error, "Unable to save wallet settings.");
      } finally {
        setSavingSettings(false);
      }
    },
    [handleAdminRequestError, loadData],
  );

  const toggleWalletSetting = useCallback(
    (key: keyof Pick<
      WalletSettings,
      | "enableWalletUsage"
      | "enableWalletTopUp"
      | "allowWalletWithCoupons"
      | "enableWalletForCodOrders"
      | "enableWalletForPickUpOrders"
      | "enableWalletForHomeDeliveryOrders"
    >) => {
      const nextSettings = {
        ...walletSettings,
        [key]: !walletSettings[key],
      };
      void saveWalletSettings(nextSettings, `${walletSettingLabels[key]} updated.`);
    },
    [saveWalletSettings, walletSettings],
  );

  useEffect(() => {
    void loadData();
  }, [loadData]);

  useEffect(() => {
    if (!selectedCouponId) {
      setRedemptions([]);
      return;
    }
    void fetchAdminWalletCouponRedemptions(selectedCouponId)
      .then(setRedemptions)
      .catch((error) => handleAdminRequestError(error, "Unable to load coupon redemption access."));
  }, [handleAdminRequestError, selectedCouponId]);

  return (
    <section className="shell section page-section">
      <div className="page-header admin-page-header">
        <div>
          <span className="eyebrow">Admin workspace</span>
          <h1>Wallet management</h1>
        </div>
      </div>
      <AdminWorkspaceNav />

      <div className="admin-orders-layout">
        <section className="store-card admin-panel admin-panel--full">
          <div className="admin-panel__heading">
            <div>
              <span className="eyebrow">Wallet settings</span>
              <h2>Checkout rules</h2>
            </div>
          </div>
          <div className="admin-wallet-live-controls">
            <ActionSwitchPill
              leftLabel="Disabled"
              rightLabel="Wallet on"
              activeSide={walletSettings.enableWalletUsage ? "right" : "left"}
              caption={walletSettings.enableWalletUsage ? "Active: Wallet on" : "Active: Wallet off"}
              tone="neutral"
              disabled={savingSettings}
              onClick={() => toggleWalletSetting("enableWalletUsage")}
              ariaLabel="Toggle wallet usage"
            />
            <ActionSwitchPill
              leftLabel="Off"
              rightLabel="Top-up on"
              activeSide={walletSettings.enableWalletTopUp ? "right" : "left"}
              caption={walletSettings.enableWalletTopUp ? "Active: Top-up on" : "Active: Top-up off"}
              tone="neutral"
              disabled={savingSettings}
              onClick={() => toggleWalletSetting("enableWalletTopUp")}
              ariaLabel="Toggle wallet top-up"
            />
            <ActionSwitchPill
              leftLabel="No coupons"
              rightLabel="Use coupons"
              activeSide={walletSettings.allowWalletWithCoupons ? "right" : "left"}
              caption={walletSettings.allowWalletWithCoupons ? "Active: Coupons allowed" : "Active: Coupons blocked"}
              tone="neutral"
              disabled={savingSettings}
              onClick={() => toggleWalletSetting("allowWalletWithCoupons")}
              ariaLabel="Toggle wallet with coupons"
            />
          </div>
          <form
            className="form-grid admin-wallet-settings-form"
            onSubmit={async (event) => {
              event.preventDefault();
              if (savingSettings) {
                return;
              }
              await saveWalletSettings(walletSettings, "Wallet settings updated.");
            }}
          >
            <label>
              Minimum order amount
              <input
                type="number"
                min={0}
                value={walletSettings.minimumOrderAmountRequiredToUseWallet}
                onChange={(event) => setWalletSettings((current) => ({ ...current, minimumOrderAmountRequiredToUseWallet: Number(event.target.value) }))}
              />
            </label>
            <label>
              Wallet usage type
              <select
                value={walletSettings.walletUsageType}
                onChange={(event) => setWalletSettings((current) => ({ ...current, walletUsageType: event.target.value as WalletSettings["walletUsageType"] }))}
              >
                <option value="PERCENTAGE">Percentage</option>
                <option value="FIXED">Fixed amount</option>
              </select>
            </label>
            <label>
              Wallet usage value
              <input
                type="number"
                min={0}
                value={walletSettings.walletUsageValue}
                onChange={(event) => setWalletSettings((current) => ({ ...current, walletUsageValue: Number(event.target.value) }))}
              />
            </label>
            <label>
              Maximum wallet per order
              <input
                type="number"
                min={0}
                value={walletSettings.maximumWalletAmountPerOrder}
                onChange={(event) => setWalletSettings((current) => ({ ...current, maximumWalletAmountPerOrder: Number(event.target.value) }))}
              />
            </label>
            <label>
              Minimum wallet balance required
              <input
                type="number"
                min={0}
                value={walletSettings.minimumWalletBalanceRequired ?? ""}
                onChange={(event) =>
                  setWalletSettings((current) => ({
                    ...current,
                    minimumWalletBalanceRequired: event.target.value === "" ? null : Number(event.target.value),
                  }))
                }
              />
            </label>
            <div className="form-grid__wide admin-wallet-live-controls">
              <ActionSwitchPill
                leftLabel="COD off"
                rightLabel="COD on"
                activeSide={walletSettings.enableWalletForCodOrders ? "right" : "left"}
                caption={walletSettings.enableWalletForCodOrders ? "Active: COD allowed" : "Active: COD blocked"}
                tone="neutral"
                disabled={savingSettings}
                onClick={() => toggleWalletSetting("enableWalletForCodOrders")}
                ariaLabel="Toggle wallet for COD orders"
              />
              <ActionSwitchPill
                leftLabel="Pickup off"
                rightLabel="Pickup on"
                activeSide={walletSettings.enableWalletForPickUpOrders ? "right" : "left"}
                caption={walletSettings.enableWalletForPickUpOrders ? "Active: Pickup allowed" : "Active: Pickup blocked"}
                tone="neutral"
                disabled={savingSettings}
                onClick={() => toggleWalletSetting("enableWalletForPickUpOrders")}
                ariaLabel="Toggle wallet for pickup orders"
              />
              <ActionSwitchPill
                leftLabel="Delivery off"
                rightLabel="Delivery on"
                activeSide={walletSettings.enableWalletForHomeDeliveryOrders ? "right" : "left"}
                caption={walletSettings.enableWalletForHomeDeliveryOrders ? "Active: Home delivery allowed" : "Active: Home delivery blocked"}
                tone="neutral"
                disabled={savingSettings}
                onClick={() => toggleWalletSetting("enableWalletForHomeDeliveryOrders")}
                ariaLabel="Toggle wallet for home delivery orders"
              />
            </div>
            <div className="admin-form-actions">
              <button className="button" type="submit" disabled={savingSettings}>
                {savingSettings ? (
                  <span className="button-loading">
                    <span className="button-loading__spinner" aria-hidden="true" />
                    Saving settings...
                  </span>
                ) : (
                  "Save settings"
                )}
              </button>
            </div>
          </form>
        </section>

        <section className="store-card admin-panel admin-panel--full">
          <div className="admin-panel__heading">
            <div>
              <span className="eyebrow">Wallet coupon form</span>
              <h2>{editingCouponId ? "Edit wallet coupon" : "Create wallet coupon"}</h2>
            </div>
          </div>
          <form
            className="form-grid"
            onSubmit={async (event) => {
              event.preventDefault();
              if (savingCoupon) {
                return;
              }
              setSavingCoupon(true);
              try {
                const payload: WalletCouponPayload =
                  couponForm.type === "ORDER_DISCOUNT"
                    ? {
                        ...couponForm,
                        amount: 0,
                        discountPercentage: couponForm.discountPercentage ?? 0,
                      }
                    : {
                        ...couponForm,
                        discountPercentage: 0,
                      };
                if (editingCouponId) {
                  await updateAdminWalletCoupon(editingCouponId, payload);
                  toast.success("Wallet coupon updated.");
                } else {
                  await createAdminWalletCoupon(payload);
                  toast.success("Wallet coupon created.");
                }
                setCouponForm(emptyCoupon);
                setEditingCouponId(null);
                await loadData();
              } catch (error) {
                handleAdminRequestError(error, "Unable to save wallet coupon.");
              } finally {
                setSavingCoupon(false);
              }
            }}
          >
            <label>
              Coupon code
              <input
                value={couponForm.code}
                onChange={(event) =>
                  setCouponForm((current) => ({ ...current, code: event.target.value.toUpperCase() }))
                }
                required
              />
            </label>
            <label>
              Type
                <select
                  value={couponForm.type}
                  onChange={(event) =>
                    setCouponForm((current) => ({
                      ...current,
                      type: event.target.value as WalletCouponPayload["type"],
                      amount: event.target.value === "ORDER_DISCOUNT" ? 0 : current.amount,
                      discountPercentage:
                        event.target.value === "ORDER_DISCOUNT" ? current.discountPercentage ?? 0 : 0,
                    }))
                  }
                >
                <option value="WALLET_TOPUP">Wallet top-up code</option>
                <option value="ORDER_CASHBACK">Checkout cashback code</option>
                <option value="ORDER_DISCOUNT">Instant discount code</option>
              </select>
            </label>
            {couponForm.type === "ORDER_DISCOUNT" ? (
              <label>
                Discount percentage
                <input
                  type="number"
                  min={1}
                  max={100}
                  value={couponForm.discountPercentage ?? 0}
                  onChange={(event) =>
                    setCouponForm((current) => ({
                      ...current,
                      discountPercentage: Number(event.target.value),
                    }))
                  }
                  required
                />
              </label>
            ) : (
              <label>
                Amount
                <input
                  type="number"
                  min={0}
                  value={couponForm.amount}
                  onChange={(event) =>
                    setCouponForm((current) => ({ ...current, amount: Number(event.target.value) }))
                  }
                  required
                />
              </label>
            )}
            <label>
              Reward delay in minutes
              <input
                type="number"
                min={0}
                value={couponForm.rewardDelayMinutes}
                onChange={(event) =>
                  setCouponForm((current) => ({
                    ...current,
                    rewardDelayMinutes: Number(event.target.value),
                  }))
                }
              />
            </label>
            <label>
              Redeem frequency
              <select
                value={couponForm.redemptionFrequency}
                onChange={(event) =>
                  setCouponForm((current) => ({
                    ...current,
                    redemptionFrequency: event.target.value as WalletCouponPayload["redemptionFrequency"],
                  }))
                }
              >
                <option value="ONCE">Only once</option>
                <option value="WEEKLY">Once every week</option>
                <option value="MONTHLY">Once every month</option>
                <option value="YEARLY">Once every year</option>
              </select>
            </label>
            <label className="form-grid__wide">
              Description
              <textarea
                rows={3}
                value={couponForm.description}
                onChange={(event) =>
                  setCouponForm((current) => ({ ...current, description: event.target.value }))
                }
              />
            </label>
            <label className="form-grid__wide">
              Assigned customer emails optional
              <textarea
                rows={3}
                value={couponForm.assignedCustomerEmails || ""}
                onChange={(event) =>
                  setCouponForm((current) => ({ ...current, assignedCustomerEmails: event.target.value }))
                }
                placeholder="Leave blank for all customers, or enter emails separated by commas/new lines."
              />
            </label>
            <div className="form-grid__wide">
              <ActionSwitchPill
                leftLabel="Inactive"
                rightLabel="Active"
                activeSide={couponForm.active ? "right" : "left"}
                caption={couponForm.active ? "Active: Coupon live" : "Active: Coupon paused"}
                tone="neutral"
                onClick={() => setCouponForm((current) => ({ ...current, active: !current.active }))}
                ariaLabel="Toggle coupon active state"
              />
            </div>
            <div className="admin-form-actions">
              <button className="button" type="submit" disabled={savingCoupon}>
                {savingCoupon ? (
                  <span className="button-loading">
                    <span className="button-loading__spinner" aria-hidden="true" />
                    {editingCouponId ? "Updating coupon..." : "Creating coupon..."}
                  </span>
                ) : editingCouponId ? (
                  "Update coupon"
                ) : (
                  "Create coupon"
                )}
              </button>
            </div>
          </form>
        </section>

        <section className="store-card admin-panel admin-panel--full">
          <div className="admin-panel__heading">
            <div>
              <span className="eyebrow">Wallet coupons</span>
              <h2>Hidden codes</h2>
            </div>
          </div>
          <div className="admin-table">
            <table>
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Type</th>
                  <th>Value</th>
                  <th>Frequency</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {coupons.map((coupon) => (
                  <tr key={coupon.id}>
                    <td>{coupon.code}</td>
                    <td>
                      {coupon.type === "ORDER_DISCOUNT"
                        ? "Instant discount"
                        : coupon.type === "ORDER_CASHBACK"
                          ? "Checkout cashback"
                          : "Wallet top-up"}
                    </td>
                    <td>
                      {coupon.type === "ORDER_DISCOUNT"
                        ? `${coupon.discountPercentage ?? 0}% off`
                        : coupon.amount}
                    </td>
                    <td>{coupon.redemptionFrequency}</td>
                    <td>{coupon.active ? "Active" : "Inactive"}</td>
                    <td>
                      <div className="admin-table-actions">
                        <button
                          className="link-button"
                          type="button"
                          onClick={() => {
                            setEditingCouponId(coupon.id);
                            setCouponForm({
                              code: coupon.code,
                              type: coupon.type,
                              amount: coupon.amount,
                              discountPercentage: coupon.discountPercentage ?? 0,
                              description: coupon.description || "",
                              assignedCustomerEmails: coupon.assignedCustomerEmails || "",
                              active: coupon.active,
                              rewardDelayMinutes: coupon.rewardDelayMinutes,
                              redemptionFrequency: coupon.redemptionFrequency || "ONCE",
                            });
                          }}
                        >
                          Edit
                        </button>
                        <button
                          className="link-button admin-danger-button"
                          type="button"
                          disabled={deletingCouponId === coupon.id}
                          onClick={async () => {
                            try {
                              setDeletingCouponId(coupon.id);
                              await deleteAdminWalletCoupon(coupon.id);
                              toast.success("Coupon deleted.");
                              if (selectedCouponId === coupon.id) {
                                setSelectedCouponId(null);
                              }
                              await loadData();
                            } catch (error) {
                              handleAdminRequestError(error, "Unable to delete wallet coupon.");
                            } finally {
                              setDeletingCouponId(null);
                            }
                          }}
                        >
                          {deletingCouponId === coupon.id ? (
                            <span className="button-loading">
                              <span className="button-loading__spinner" aria-hidden="true" />
                              Deleting...
                            </span>
                          ) : (
                            "Delete"
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {!coupons.length ? (
                  <tr>
                    <td colSpan={6}>No wallet coupons created yet.</td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </section>

        <section className="store-card admin-panel admin-panel--full">
          <div className="admin-panel__heading">
            <div>
              <span className="eyebrow">Redeem access</span>
              <h2>Allow selected users to redeem again</h2>
            </div>
          </div>
          <div className="form-grid">
            <label>
              Coupon
              <select
                value={selectedCouponId ?? ""}
                onChange={(event) => setSelectedCouponId(Number(event.target.value) || null)}
              >
                <option value="">Select coupon</option>
                {coupons.map((coupon) => (
                  <option key={coupon.id} value={coupon.id}>
                    {coupon.code} - {coupon.type === "ORDER_DISCOUNT" ? `${coupon.discountPercentage ?? 0}% off` : coupon.type}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className="admin-table">
            <table>
              <thead>
                <tr>
                  <th>User</th>
                  <th>Email</th>
                  <th>Redeemed</th>
                  <th>Allowed</th>
                  <th>Remaining</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {redemptions.map((entry) => (
                  <tr key={entry.id}>
                    <td>{entry.userName}</td>
                    <td>{entry.userEmail}</td>
                    <td>{entry.redeemedCount}</td>
                    <td>{entry.allowedRedemptions}</td>
                    <td>{entry.remainingRedemptions}</td>
                    <td>
                      <button
                        className="link-button"
                        type="button"
                        disabled={grantingCouponId === entry.userId}
                        onClick={async () => {
                          if (!selectedCouponId) {
                            return;
                          }
                          try {
                            setGrantingCouponId(entry.userId);
                            await grantAdminWalletCouponRedemptions(selectedCouponId, {
                              userId: entry.userId,
                              additionalRedemptions: 1,
                            });
                            toast.success("One more redeem granted.");
                            const refreshed = await fetchAdminWalletCouponRedemptions(selectedCouponId);
                            setRedemptions(refreshed);
                          } catch (error) {
                            handleAdminRequestError(error, "Unable to update coupon redeem access.");
                          } finally {
                            setGrantingCouponId(null);
                          }
                        }}
                      >
                        {grantingCouponId === entry.userId ? (
                          <span className="button-loading">
                            <span className="button-loading__spinner" aria-hidden="true" />
                            Updating...
                          </span>
                        ) : (
                          "Allow 1 more"
                        )}
                      </button>
                    </td>
                  </tr>
                ))}
                {!redemptions.length ? (
                  <tr>
                    <td colSpan={6}>No user has redeemed the selected coupon yet.</td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </section>
  );
};

export default AdminWalletPage;
